import React, { useState, useEffect } from 'react';
import { ArabicWord } from '../types';
import { fetchRandomWords } from '../services/wordService';
import { getEarliestWordDate, getCurrentLanguage } from '../constants';
import LoadingOverlay from './LoadingOverlay';
import Navigation from './Navigation';
import { isMobilePhone } from '../utils/deviceUtils';
import '../styles/WordQuiz.css';

interface WordQuizProps {
  // Optional props can be added here
}

interface MatchItem {
  id: string;
  text: string;
  matched: boolean;
  type: 'english' | 'romanization';
  originalIndex: number;
  audioUrl?: string;
}

const WordQuiz: React.FC<WordQuizProps> = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [englishItems, setEnglishItems] = useState<MatchItem[]>([]);
  const [romanizationItems, setRomanizationItems] = useState<MatchItem[]>([]);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [allMatched, setAllMatched] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<MatchItem | null>(null);

  useEffect(() => {
    loadQuizWords();
  }, []);

  useEffect(() => {
    document.title = `${getCurrentLanguage()} Word Quiz`;
  }, []);

  useEffect(() => {
    // Check if all items are matched
    if (englishItems.length > 0 && englishItems.every(item => item.matched)) {
      setAllMatched(true);
    }
  }, [englishItems]);

  const loadQuizWords = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch random words from the last 30 days
      const words = await fetchRandomWords(getEarliestWordDate());
      
      // Create match items from the words
      const english: MatchItem[] = words.map((word, index) => ({
        id: `english-${word.id}`,
        text: word.translation,
        matched: false,
        type: 'english',
        originalIndex: index
      }));
      
      const romanization: MatchItem[] = words.map((word, index) => ({
        id: `romanization-${word.id}`,
        text: word.romanization,
        matched: false,
        type: 'romanization',
        originalIndex: index,
        audioUrl: word.audioUrl
      }));
      
      // Shuffle both arrays
      setEnglishItems(shuffleArray([...english]));
      setRomanizationItems(shuffleArray([...romanization]));
      setLoading(false);
    } catch (err) {
      setError('Failed to load quiz words');
      setLoading(false);
      console.error('Error loading quiz words:', err);
    }
  };

  const shuffleArray = <T,>(array: T[]): T[] => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };


  const handleItemClick = (item: MatchItem) => {
    if (item.matched) return;
    
    if (!selectedItem) {
      // First item selected
      setSelectedItem(item);
    } else if (selectedItem.type === item.type) {
      // Selected same type item, just update selection
      setSelectedItem(item);
    } else {
      // Selected opposite type item, check for match
      const isCorrectMatch = selectedItem.originalIndex === item.originalIndex;
      
      if (isCorrectMatch) {
        // Update the matched status
        if (selectedItem.type === 'english') {
          setEnglishItems(prev => 
            prev.map(i => 
              i.id === selectedItem.id ? { ...i, matched: true } : i
            )
          );
          setRomanizationItems(prev => 
            prev.map(i => 
              i.id === item.id ? { ...i, matched: true } : i
            )
          );
        } else {
          setRomanizationItems(prev => 
            prev.map(i => 
              i.id === selectedItem.id ? { ...i, matched: true } : i
            )
          );
          setEnglishItems(prev => 
            prev.map(i => 
              i.id === item.id ? { ...i, matched: true } : i
            )
          );
        }
        
        // Show success message
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 1500);
      } else {
        // Show error message
        setShowError(true);
        setTimeout(() => setShowError(false), 1500);
      }
      
      // Reset selection
      setSelectedItem(null);
    }
  };

  const playAudio = (audioUrl?: string): void => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.play();
    }
  };

  const resetQuiz = () => {
    setAllMatched(false);
    setSelectedItem(null);
    loadQuizWords();
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>{getCurrentLanguage()} Word of the Day</h1>
        <Navigation />
      </header>
      
      <div className="word-quiz-container">
        <h2>Match the Words</h2>
        <p className="quiz-instructions">
          Click or tap an English word, then click or tap its matching romanization
        </p>
      
      {loading ? (
        <LoadingOverlay isLoading={loading} />
      ) : error ? (
        <p className="error">{error}</p>
      ) : (
        <div className="quiz-content">
          {showSuccess && <div className="feedback-banner success">Correct match!</div>}
          {showError && <div className="feedback-banner error">Try again</div>}
          
          {allMatched && (
            <div className="quiz-complete">
              <h3>Congratulations!</h3>
              <p>You've matched all the words correctly.</p>
              <button className="reset-button" onClick={resetQuiz}>Try Another Quiz</button>
            </div>
          )}
          
          <div className="quiz-columns">
            <div className="quiz-column">
              <h3>English</h3>
              <div className="quiz-items">
                {englishItems.map(item => (
                  <div
                    key={item.id}
                    className={`quiz-item english-item ${item.matched ? 'matched' : ''} 
                      ${selectedItem?.id === item.id ? 'selected' : ''}`}
                    data-id={item.id}
                    onClick={() => handleItemClick(item)}
                  >
                    {item.text}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="quiz-column">
              <h3>Romanization</h3>
              <div className="quiz-items">
                {romanizationItems.map(item => (
                  <div
                    key={item.id}
                    className={`quiz-item romanization-item ${item.matched ? 'matched' : ''} 
                      ${selectedItem?.id === item.id ? 'selected' : ''}`}
                    data-id={item.id}
                    onClick={() => handleItemClick(item)}
                  >
                    <div className="romanization-content">
                      {item.text}
                      {item.audioUrl && (
                        <button 
                          className="audio-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            playAudio(item.audioUrl);
                          }}
                          aria-label="Play pronunciation"
                        > 
                          🔊
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
      
      <footer className="App-footer">
        <p>&copy; {new Date().getFullYear()} {getCurrentLanguage()} Word of the Day</p>
      </footer>
    </div>
  );
};

export default WordQuiz;